import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LOGO_ICON, IMAGES, CHAPTERS } from "../lib/site";

const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const Chapter = ({ ch, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const flip = index % 2 === 1;

  return (
    <div
      ref={ref}
      data-testid={`chapter-${ch.no}`}
      className="grid grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-16"
    >
      {/* Text */}
      <div
        className={`md:col-span-5 ${
          flip ? "md:order-2 md:col-start-8" : "md:order-1"
        }`}
      >
        <Reveal>
          <span className="font-display text-7xl font-light text-gold/45 md:text-8xl">
            {ch.no}
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <h3 className="mt-2 font-display text-4xl font-light text-ink md:text-5xl">
            {ch.title}
          </h3>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="deco-divider my-6 w-24" />
        </Reveal>
        <Reveal delay={0.25}>
          <p className="max-w-md font-body text-base font-light leading-relaxed text-ink/70">
            {ch.body}
          </p>
        </Reveal>
      </div>

      {/* Image */}
      <div
        className={`md:col-span-6 ${
          flip ? "md:order-1 md:col-start-1" : "md:order-2 md:col-start-7"
        }`}
      >
        <Reveal delay={0.15}>
          <div className="relative overflow-hidden rounded-t-[140px] border border-gold/25">
            <motion.img
              style={{ y: imgY, scale: 1.15 }}
              src={IMAGES[ch.image]}
              alt={ch.title}
              className="h-[420px] w-full object-cover md:h-[560px]"
            />
            <div className="absolute inset-0 bg-ink/10" />
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative mx-auto max-w-[1500px] px-8 py-32 md:px-16 md:py-48"
    >
      <div className="mb-24 flex flex-col items-start gap-8 md:flex-row md:items-end md:justify-between">
        <div>
          <Reveal>
            <p className="mb-4 font-body text-xs uppercase tracking-[0.4em] text-gold">
              About Us
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="max-w-2xl font-display text-4xl font-light leading-tight text-ink md:text-6xl">
              A manifesto for those who
              <span className="italic text-gold"> build boldly.</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <img
            src={LOGO_ICON}
            alt="Metropolis icon"
            className="h-24 w-24 rounded-full opacity-80 ring-1 ring-gold/30 md:h-32 md:w-32"
          />
        </Reveal>
      </div>

      <div className="flex flex-col gap-28 md:gap-40">
        {CHAPTERS.map((ch, i) => (
          <Chapter key={ch.no} ch={ch} index={i} />
        ))}
      </div>
    </section>
  );
};
